import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { 
  Plus, 
  Search, 
  AlertTriangle, 
  Package,
  Edit,
  TrendingDown,
  TrendingUp,
  ArrowUp,
  ArrowDown,
  Trash2,
  ChevronDown,
  Download,
  Upload,
  FileText
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService, RawMaterial } from '../../services/dataService';
import { useCategories } from '../../hooks/useCategories';
import { CSVUtils } from '../../utils/csvUtils';
import AddRawMaterialModal from './AddRawMaterialModal';
import EditRawMaterialModal from './EditRawMaterialModal';
import StockTransactionModal from './StockTransactionModal';
import CSVImportModal from './CSVImportModal';

// Custom Animated Select Component
const AnimatedSelect: React.FC<{
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  className?: string;
}> = ({ value, onChange, options, placeholder, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const selectRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    if (isOpen) {
      setIsClosing(true);
      setTimeout(() => {
        setIsOpen(false);
        setIsClosing(false);
      }, 300);
    } else {
      if (selectRef.current) {
        const rect = selectRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width
        });
      }
      setIsOpen(true);
    }
  };

  const handleSelect = (option: string) => {
    onChange(option);
    handleToggle();
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
      if (isOpen) {
        handleToggle();
      }
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const selectedOption = options.find(option => option === value) || placeholder || 'Select...';

  return (
    <>
      <div ref={selectRef} className={`custom-select ${className}`}>
        <div 
          className="custom-select-trigger animate-input-focus"
          onClick={handleToggle}
        >
          <span className="truncate">{selectedOption}</span>
          <ChevronDown 
            className={`w-4 h-4 text-gray-400 dark:text-gray-500 transition-transform duration-300 ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </div>
      </div>
      {isOpen && createPortal(
        <div 
          className={`custom-select-dropdown-portal ${isClosing ? 'closing' : 'open'}`}
          style={{
            position: 'absolute',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            zIndex: 99999
          }}
        >
          {options.map((option) => (
            <div
              key={option}
              className={`custom-select-option ${
                option === value ? 'selected' : ''
              }`}
              onClick={() => handleSelect(option)}
            >
              {option === 'all' ? 'All Categories' : option}
            </div>
          ))}
        </div>,
        document.body
      )}
    </>
  );
};

const RawMaterials: React.FC = () => {
  const { tenantData } = useAuth();
  const { getCategoryNamesWithAll } = useCategories('rawMaterial');
  const [rawMaterials, setRawMaterials] = useState<RawMaterial[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState<RawMaterial | null>(null);
  const [stockAction, setStockAction] = useState<'in' | 'out'>('in');

  const categories = getCategoryNamesWithAll();

  const loadRawMaterials = useCallback(async () => {
    try {
      setLoading(true);
      const materials = await dataService.getRawMaterials(tenantData!.id);
      setRawMaterials(materials);
    } catch (error) {
      console.error('Error loading raw materials:', error);
    } finally {
      setLoading(false);
    }
  }, [tenantData]);

  useEffect(() => {
    if (tenantData?.id) {
      loadRawMaterials();
    }
  }, [tenantData, loadRawMaterials]);

  const getStockStatus = (current: number, min: number, max: number) => {
    if (current <= min) return 'low';
    if (current >= max) return 'high';
    if (current <= min * 1.5) return 'medium';
    return 'good';
  };

  const getStockColor = (status: string) => {
    switch (status) {
      case 'low': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'good': return 'bg-green-100 text-green-800';
      case 'high': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStockIcon = (status: string) => {
    switch (status) {
      case 'low': return <TrendingDown className="w-4 h-4" />;
      case 'medium': return <AlertTriangle className="w-4 h-4" />;
      case 'good': return <TrendingUp className="w-4 h-4" />;
      case 'high': return <ArrowUp className="w-4 h-4" />;
      default: return <Package className="w-4 h-4" />;
    }
  };

  const filteredMaterials = rawMaterials.filter(material => {
    const matchesSearch = material.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (material.supplier && material.supplier.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = categoryFilter === 'all' || material.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const lowStockMaterials = rawMaterials.filter(material => 
    getStockStatus(material.currentStock, material.minStock, material.maxStock) === 'low'
  );

  const handleStockIn = (material: RawMaterial) => {
    setSelectedMaterial(material);
    setStockAction('in');
    setShowStockModal(true);
  };

  const handleStockOut = (material: RawMaterial) => {
    setSelectedMaterial(material);
    setStockAction('out');
    setShowStockModal(true);
  };

  const handleStockTransaction = async () => {
    await loadRawMaterials();
    setShowStockModal(false);
    setSelectedMaterial(null);
  };

  const handleAddMaterial = async () => {
    await loadRawMaterials();
    setShowAddModal(false);
  };

  const handleEdit = (material: RawMaterial) => {
    setSelectedMaterial(material);
    setShowEditModal(true);
  };

  const handleEditSuccess = async () => {
    await loadRawMaterials();
    setShowEditModal(false);
    setSelectedMaterial(null);
  };

  const handleDelete = async (material: RawMaterial) => {
    const confirmMessage = `Are you sure you want to delete "${material.name}"?\n\nThis action cannot be undone and will permanently remove the raw material from your system.\n\nWarning: This may affect menu items that use this material.`;
    
    if (window.confirm(confirmMessage)) {
      try {
        await dataService.delete(tenantData!.id, 'rawMaterials', material.id!);
        await loadRawMaterials();
      } catch (error) {
        console.error('Error deleting raw material:', error);
        alert('Failed to delete raw material. Please try again.');
      }
    }
  };

  const handleDownloadTemplate = () => {
    const template = CSVUtils.generateTemplate('rawMaterial');
    CSVUtils.downloadCSV(template, 'raw-materials-template.csv');
  };

  const handleExportCSV = (exportAll: boolean = false) => {
    try {
      const dataToExport = exportAll ? rawMaterials : filteredMaterials;
      console.log('Exporting data:', dataToExport.length, 'items');
      console.log('Sample item:', dataToExport[0]);
      
      const csvContent = CSVUtils.exportToCSV(dataToExport, 'rawMaterial');
      console.log('Generated CSV content length:', csvContent.length);
      
      const filename = exportAll ? 'raw-materials-all.csv' : 'raw-materials-filtered.csv';
      CSVUtils.downloadCSV(csvContent, filename);
      console.log('Download initiated for:', filename);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export CSV. Please try again.');
    }
  };

  const handleImportSuccess = async () => {
    await loadRawMaterials();
    setShowImportModal(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-page-enter">
      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 sm:justify-end animate-fade-in-up">
        {/* CSV Actions */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          <button 
            onClick={handleDownloadTemplate}
            className="btn-secondary w-full sm:w-auto flex items-center justify-center animate-button-hover"
          >
            <FileText className="w-4 h-4 mr-2" />
            Download Template
          </button>
          
          <div className="relative group">
            <button 
              onClick={() => handleExportCSV(false)}
              className="btn-secondary w-full sm:w-auto flex items-center justify-center animate-button-hover"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </button>
            <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
              <button
                onClick={() => handleExportCSV(false)}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-t-lg"
              >
                Export Filtered Results ({filteredMaterials.length} items)
              </button>
              <button
                onClick={() => handleExportCSV(true)}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-b-lg"
              >
                Export All ({rawMaterials.length} items)
              </button>
            </div>
          </div>
          
          <button 
            onClick={() => setShowImportModal(true)}
            className="btn-secondary w-full sm:w-auto flex items-center justify-center animate-button-hover"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import CSV
          </button>
        </div>
        
        {/* Add Material Button */}
        <button 
          onClick={() => setShowAddModal(true)}
          className="btn-primary w-full sm:w-auto flex items-center justify-center animate-button-hover"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Material
        </button>
      </div>

      {/* Low Stock Alert */}
      {lowStockMaterials.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 animate-fade-in-up animation-delay-200">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="w-6 h-6 text-red-500" />
            <div>
              <h3 className="text-lg tablet:text-xl font-medium text-red-800">
                Low Stock Alert
              </h3>
              <p className="text-red-700 text-base tablet:text-lg">
                {lowStockMaterials.length} material{lowStockMaterials.length > 1 ? 's' : ''} running low on stock
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4 sm:p-6 animate-fade-in-up animation-delay-300">
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <div className="search-input-icon">
                <Search />
              </div>
              <input
                type="text"
                placeholder="Search materials by name or supplier..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10 sm:pl-10 animate-input-focus"
              />
            </div>
          </div>

          {/* Category Filter */}
          <div className="w-full sm:w-48">
            <AnimatedSelect
              value={categoryFilter}
              onChange={setCategoryFilter}
              options={categories}
              placeholder="All Categories"
              className="category-filter"
            />
          </div>
        </div>
      </div>

            {/* Materials Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
        {filteredMaterials.map((material, index) => {
          const stockStatus = getStockStatus(material.currentStock, material.minStock, material.maxStock);
          return (
            <div 
              key={material.id} 
              className={`bg-white/90 dark:bg-gray-900/80 backdrop-blur-md rounded-lg shadow-sm dark:shadow-lg border border-gray-200 dark:border-gray-800 overflow-hidden animate-card-enter animate-card-hover ${
                index < 5 ? `animate-stagger-${index + 1}` : 'animate-stagger-5'
              }`}
            >
              {/* Header */}
              <div className="p-3 sm:p-4 pb-2 border-b border-gray-100 dark:border-gray-800">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate mb-1">
                      {material.name}
                    </h3>
                    <div className="flex items-center space-x-1">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200">
                        {material.category}
                      </span>
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getStockColor(stockStatus)} border border-gray-200 dark:border-gray-700`}>
                        {getStockIcon(stockStatus)}
                        <span className="ml-1 capitalize">{stockStatus}</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stock Information */}
              <div className="p-3 sm:p-4 space-y-2">
                {/* Current Stock - Prominent */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded p-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Stock</span>
                    <div className="text-right">
                      <div className="text-lg font-bold text-gray-900 dark:text-white">
                        {Number(material.currentStock).toFixed(2)} <span className="text-xs font-normal text-gray-600 dark:text-gray-400">{material.subUnit || material.baseUnit}</span>
                      </div>
                      {material.subUnit && material.mainUnit && material.subUnitsPerMainUnit && material.subUnitsPerMainUnit > 1 && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {Math.floor(material.currentStock / material.subUnitsPerMainUnit)} {material.mainUnit}s + {material.currentStock % material.subUnitsPerMainUnit}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-center">
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Min</div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">{material.minStock}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Cost</div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">
                      ₹{(material.costPerSubUnit || material.costPerUnit || 0).toFixed(2)}
                    </div>
                  </div>
                </div>

                {/* Additional Info */}
                <div className="space-y-1 pt-2 border-t border-gray-100 dark:border-gray-800">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500 dark:text-gray-400">Supplier</span>
                    <span className="text-gray-900 dark:text-white font-medium truncate ml-2">{material.supplier || 'Not specified'}</span>
                  </div>
                  
                  {material.location && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-500 dark:text-gray-400">Location</span>
                      <span className="text-gray-700 dark:text-gray-200 truncate ml-2">{material.location}</span>
                    </div>
                  )}

                  {material.lastRestocked && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-500 dark:text-gray-400">Restocked</span>
                      <span className="text-gray-700 dark:text-gray-200">
                        {new Date(material.lastRestocked).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                  )}

                  {/* Conversion Info */}
                  {material.subUnit && material.mainUnit && material.subUnitsPerMainUnit && material.subUnitsPerMainUnit > 1 && (
                    <div className="bg-blue-50 rounded p-1.5 text-xs text-blue-700">
                      <span className="font-medium">1 {material.mainUnit} = {material.subUnitsPerMainUnit} {material.subUnit}s</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="p-3 sm:p-4 pt-0">
                <div className="flex space-x-1">
                  <button
                    onClick={() => handleStockIn(material)}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white px-2 py-1.5 rounded text-xs font-medium transition-colors duration-200 flex items-center justify-center space-x-1"
                  >
                    <ArrowUp className="w-3 h-3" />
                    <span>In</span>
                  </button>
                  <button
                    onClick={() => handleStockOut(material)}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white px-2 py-1.5 rounded text-xs font-medium transition-colors duration-200 flex items-center justify-center space-x-1"
                  >
                    <ArrowDown className="w-3 h-3" />
                    <span>Out</span>
                  </button>
                  <button 
                    onClick={() => handleEdit(material)}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-2 py-1.5 rounded text-xs font-medium transition-colors duration-200 flex items-center justify-center"
                  >
                    <Edit className="w-3 h-3" />
                  </button>
                  <button 
                    onClick={() => handleDelete(material)}
                    className="bg-red-100 hover:bg-red-200 text-red-700 px-2 py-1.5 rounded text-xs font-medium transition-colors duration-200 flex items-center justify-center"
                    title="Delete Material"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredMaterials.length === 0 && (
        <div className="col-span-full">
                      <div className="text-center py-12 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
            <Package className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No materials found</h3>
            <p className="text-sm text-gray-600 mb-4 max-w-md mx-auto">
              {searchTerm || categoryFilter !== 'all' 
                ? 'No materials match your current search or filter criteria.' 
                : 'Get started by adding your first raw material to begin tracking inventory.'
              }
            </p>
            <button 
              onClick={() => setShowAddModal(true)}
              className="btn-primary text-sm px-4 py-2"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add First Material
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      {showAddModal && (
        <AddRawMaterialModal
          onClose={() => setShowAddModal(false)}
          onSuccess={handleAddMaterial}
        />
      )}

      {showEditModal && selectedMaterial && (
        <EditRawMaterialModal
          material={selectedMaterial}
          onClose={() => setShowEditModal(false)}
          onSuccess={handleEditSuccess}
        />
      )}

      {showStockModal && selectedMaterial && (
        <StockTransactionModal
          material={selectedMaterial}
          action={stockAction}
          onClose={() => setShowStockModal(false)}
          onSuccess={handleStockTransaction}
        />
      )}

      {showImportModal && (
        <CSVImportModal
          type="rawMaterial"
          onClose={() => setShowImportModal(false)}
          onSuccess={handleImportSuccess}
        />
      )}
    </div>
  );
};

export default RawMaterials; 